// referenceservice.ts
import { axiosInstance, API_BASE_URL } from "../config/axiosInstance";
import {
  PostBloodGroupRequestDto,
  GetBloodGroupParamsDto,
  BloodGroupResponseDto,
  GetCardParamsDto,
  CreateCardTableDataRequestDto,
  UpdateCardTableDataRequestDto,
  DeleteCardTableDataRequestDto,
  GetCardTableDataParamsDto,
  CardResponseDto,
  GetAllCitiesParamsDto,
  GetAllCitiesResponseDto,
  GetConnectionOfMedCardParamsDto,
  CreateConnectionOfMedcardsRequestDto,
  ConnectionOfMedcardResponseDto,
  GetAllCountriesResponseDto,
  GetCountyAndCityPathParamsDto,
  GetCountyAndCityResponseDto,
  GetCrmMainByPoolDisplayPathParamsDto,
  CrmMainResponseDto,
  GetAllDepartmentsByOffIdRequestDto,
  GetBranchListFromDepartmentRequestDto,
  GetDepartmentNamesRequestDto,
  DepartmentResponseDto,
  BranchResponseDto,
  PostDoctorListRequestDto,
  DoctorResponseDto,
  GetAnaesthetistListResponseDto,
  CreateFavoriteMedicalCardRequestDto,
  DeleteFavoriteMedicalCardPathParamsDto,
  FavoriteMedicalCardResponseDto,
  GetFavoriteMedicalCardResponseDto,
  GetAllHospitalBuildingsPathParamsDto,
  GetAllHospitalBuildingsResponseDto,
  GetAllHospitalFloorsByHbIdPathParamsDto,
  GetAllHospitalFloorsByHbIdResponseDto,
  HospitalFloorResponseDto,
  GetAllHospitalRoomsPathParamsDto,
  GetAllHospitalRoomsResponseDto,
  GetAllHrEducationByActivePathParamsDto,
  GetAllHrEducationByActiveResponseDto,
  GetAllIdentificationTypesResponseDto,
  GetAllInsuredNearnessesResponseDto,
  GetAllInsuredTypesResponseDto,
  GetAllMcParametersResponseDto,
  GetBulkMcParameterDetailsRequestDto,
  GetBulkMcParameterDetailsResponseDto,
  GetAllMedCardsResponseDto,
  GetPatientNameSurnamesRequestDto,
  GetPatientNameSurnamesResponseDto,
  GetAllPatientDischargeTypeResponseDto,
  PostPatientDiseaseRequestDto,
  PatientDiseaseResponseDto,
  GetPatientGroupByPtypePathParamsDto,
  GetPatientGroupByPtypeResponseDto,
  GetAllProfessionListResponseDto,
  GetAllReferenceListResponseDto,
  GetSpecialCodeListByPartNumberPathParamsDto,
  GetSpecialCodeListByPartNumberResponseDto,
  GetAllUsersResponseDto,
  CardApiResponse,
  GetPatientTypesParamsDto,
  GetPatientTypesResponseDto,
  UpdatePatientMonitoringRequestDto,
  PatientMonitoringResponseDto,
  CreatePatientMonitoringRequestDto,
  GetPatientMonitoringRequestDto,
  GetPatientMonitoringListResponseDto,
  UpdatePatientVitalRecordRequestDto,
  CreatePatientVitalRecordRequestDto,
  GetPatientVitalRecordByIdResponseDto,
  GetPatientVitalRecordResponseDto,
  GetPatientVitalRecordRequestDto,
  GetNewbornTraceRequestDto,
  GetNewbornTraceResponseDto,
  AddNewbornTraceRequestDto,
  UpdateNewbornTraceRequestDto,
  NewbornTraceItemDto,
  GetPatientHeightWeightTraceRequestDto,
  GetPatientHeightWeightTraceResponseDto,
  GetPatientHeightWeightTraceByIdResponseDto,
  AddPatientHeightWeightTraceRequestDto,
  UpdatePatientHeightWeightTraceRequestDto,
  GetAdditionalInfoFieldsRequestDto,
  GetAdditionalInfoFieldsResponseDto,
  GetPatientProcessRequestDto,
  GetPatientProcessListResponseDto,
  UpdateBradenFileRequestDto,
  McBradenFileResponseDto,
  McBradenFileItemsResponseDto,
  UploadBradenFileRequestDto,
  CreatePediatricAssessmentRequestDto,
  UpdatePediatricAssessmentRequestDto,
  GetPediatricAssessmentRequestDto,
  GetPediatricAssessmentResponseDto,
  AuthorizedWarehouseDto,
  GetAuthorizedWarehousesResponseDto,
} from "./referenceServicesDtos";

const inflightGetCardTableDataRequests = new Map<string, Promise<CardApiResponse>>();

const stableStringify = (obj: any) => {
  if (!obj || typeof obj !== "object") return JSON.stringify(obj);
  const keys = Object.keys(obj).sort();
  const normalized: any = {};
  for (const k of keys) normalized[k] = obj[k];
  return JSON.stringify(normalized);
};

export const referenceService = {
  bloodGroup: {
    post: async (
      data: PostBloodGroupRequestDto
    ): Promise<BloodGroupResponseDto> => {
      try {
        const response = await axiosInstance.post<BloodGroupResponseDto>(
          `${API_BASE_URL}/reference/api/BloodGroup`,
          data
        );
        return response.data;
      } catch (error) {
        console.error("Error posting blood group:", error);
        throw error;
      }
    },
    get: async (
      params: GetBloodGroupParamsDto
    ): Promise<BloodGroupResponseDto[]> => {
      try {
        const response = await axiosInstance.get<BloodGroupResponseDto[]>(
          `${API_BASE_URL}/reference/api/BloodGroup`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching blood group:", error);
        throw error;
      }
    },
  },

  card: {
    get: async (params: GetCardParamsDto): Promise<CardResponseDto[]> => {
      try {
        const response = await axiosInstance.get<CardResponseDto[]>(
          `${API_BASE_URL}/reference/api/Card`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching card:", error);
        throw error;
      }
    },
    post: async (
      data: CreateCardTableDataRequestDto
    ): Promise<CardResponseDto> => {
      try {
        const response = await axiosInstance.post<CardResponseDto>(
          `${API_BASE_URL}/reference/api/Card`,
          data
        );
        return response.data;
      } catch (error) {
        console.error("Error creating card table data:", error);
        throw error;
      }
    },
    put: async (
      data: UpdateCardTableDataRequestDto
    ): Promise<CardResponseDto> => {
      try {
        const response = await axiosInstance.put<CardResponseDto>(
          `${API_BASE_URL}/reference/api/Card`,
          data
        );
        return response.data;
      } catch (error) {
        console.error("Error updating card table data:", error);
        throw error;
      }
    },
    delete: async (data: DeleteCardTableDataRequestDto): Promise<void> => {
      try {
        await axiosInstance.delete(`${API_BASE_URL}/reference/api/Card`, {
          data,
        });
      } catch (error) {
        console.error("Error deleting card table data:", error);
        throw error;
      }
    },
    getCardTableData: async (params: GetCardTableDataParamsDto): Promise<CardApiResponse> => {
      const key = stableStringify(params);
      const existing = inflightGetCardTableDataRequests.get(key);
      if (existing) return existing;

      const requestPromise = axiosInstance
        .get<CardApiResponse>(`${API_BASE_URL}/reference/api/Card/GetCardTableData`, { params })
        .then((r) => r.data)
        .finally(() => {
          inflightGetCardTableDataRequests.delete(key);
        });

      inflightGetCardTableDataRequests.set(key, requestPromise);
      return requestPromise;
    },
  },

  city: {
    getAllCities: async (
      params: GetAllCitiesParamsDto
    ): Promise<GetAllCitiesResponseDto> => {
      try {
        const response = await axiosInstance.get<GetAllCitiesResponseDto>(
          `${API_BASE_URL}/reference/api/City/GetAllCities`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching all cities:", error);
        throw error;
      }
    },
  },

  connectionsOfMedcard: {
    getConnectionOfMedCard: async (
      params: GetConnectionOfMedCardParamsDto
    ): Promise<ConnectionOfMedcardResponseDto> => {
      try {
        const response =
          await axiosInstance.get<ConnectionOfMedcardResponseDto>(
            `${API_BASE_URL}/reference/api/ConnectionsOfMedcard/GetConnectionOfMedCard`,
            { params }
          );
        return response.data;
      } catch (error) {
        console.error("Error fetching connection of med card:", error);
        throw error;
      }
    },

    createConnectionOfMedCard: async (
      data: CreateConnectionOfMedcardsRequestDto
    ): Promise<ConnectionOfMedcardResponseDto> => {
      try {
        const response =
          await axiosInstance.post<ConnectionOfMedcardResponseDto>(
            `${API_BASE_URL}/reference/api/ConnectionsOfMedcard/CreateConnectionOfMedCard`,
            data
          );
        return response.data;
      } catch (error) {
        console.error("Error creating connection of med card:", error);
        throw error;
      }
    },
  },

  country: {
    getAllCountries: async (): Promise<GetAllCountriesResponseDto> => {
      try {
        const response = await axiosInstance.get<GetAllCountriesResponseDto>(
          `${API_BASE_URL}/reference/api/Country/GetAllCountries`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching all countries:", error);
        throw error;
      }
    },
  },

  county: {
    getCountyAndCity: async (
      params: GetCountyAndCityPathParamsDto
    ): Promise<GetCountyAndCityResponseDto> => {
      try {
        const response = await axiosInstance.get<GetCountyAndCityResponseDto>(
          `${API_BASE_URL}/reference/api/County/GetCountyAndCity/${params.offId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching county and city:", error);
        throw error;
      }
    },
  },

  crmMain: {
    getCrmMainByPoolDisplay: async (
      params: GetCrmMainByPoolDisplayPathParamsDto
    ): Promise<CrmMainResponseDto> => {
      try {
        const response = await axiosInstance.get<CrmMainResponseDto>(
          `${API_BASE_URL}/reference/api/CrmMain/GetCrmMainByPoolDisplay/${params.poolDisplay}/${params.assId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching CRM main by pool display:", error);
        throw error;
      }
    },
  },

  department: {
    getDepartmentNames: async (
      data: GetDepartmentNamesRequestDto
    ): Promise<Record<number, string>> => {
      try {
        const response = await axiosInstance.post<{ data: Record<number, string> }>(
          `${API_BASE_URL}/reference/api/Department/GetDepartmentNames`,
          data
        );
        return response.data.data;
      } catch (error) {
        console.error("Error fetching department names:", error);
        throw error;
      }
    },
    getAllDepartmentsByOffId: async (
      params: GetAllDepartmentsByOffIdRequestDto
    ): Promise<DepartmentResponseDto[]> => {
      try {
        const response = await axiosInstance.get<{ data: DepartmentResponseDto[] }>(
          `${API_BASE_URL}/reference/api/Department/GetAllDepartmentsByOffId`,
          { params }
        );
        return response.data.data;
      } catch (error) {
        console.error("Error fetching all departments by office ID:", error);
        throw error;
      }
    },
    getBranchListFromDepartment: async (
      params: GetBranchListFromDepartmentRequestDto
    ): Promise<BranchResponseDto[]> => {
      try {
        const response = await axiosInstance.get<{ data: BranchResponseDto[] }>(
          `${API_BASE_URL}/reference/api/Department/GetBranchListFromDepartment`,
          { params }
        );
        return response.data.data;
      } catch (error) {
        console.error("Error fetching branch list from department:", error);
        throw error;
      }
    },
  },

  doctorList: {
    post: async (
      data: PostDoctorListRequestDto
    ): Promise<DoctorResponseDto> => {
      try {
        const response = await axiosInstance.post<DoctorResponseDto>(
          `${API_BASE_URL}/reference/api/DoctorList`,
          data
        );
        return response.data;
      } catch (error) {
        console.error("Error posting doctor list:", error);
        throw error;
      }
    },
    getAnaesthetistList: async (): Promise<GetAnaesthetistListResponseDto> => {
      try {
        const response =
          await axiosInstance.get<GetAnaesthetistListResponseDto>(
            `${API_BASE_URL}/reference/api/DoctorList/GetAnaesthetistList`
          );
        return response.data;
      } catch (error) {
        console.error("Error fetching anaesthetist list:", error);
        throw error;
      }
    },
  },

  favoriteMedicalCard: {
    getFavoriteMedicalCard:
      async (): Promise<GetFavoriteMedicalCardResponseDto> => {
        try {
          const response =
            await axiosInstance.get<GetFavoriteMedicalCardResponseDto>(
              `${API_BASE_URL}/reference/api/FavoriteMedicalCard/GetFavoriteMedicalCard`
            );
          return response.data;
        } catch (error) {
          console.error("Error fetching favorite medical card:", error);
          throw error;
        }
      },
    createFavoriteMedicalCard: async (
      data: CreateFavoriteMedicalCardRequestDto
    ): Promise<FavoriteMedicalCardResponseDto> => {
      try {
        const response =
          await axiosInstance.post<FavoriteMedicalCardResponseDto>(
            `${API_BASE_URL}/reference/api/FavoriteMedicalCard/CreateFavoriteMedicalCard`,
            data
          );
        return response.data;
      } catch (error) {
        console.error("Error creating favorite medical card:", error);
        throw error;
      }
    },
    deleteFavoriteMedicalCard: async (
      params: DeleteFavoriteMedicalCardPathParamsDto
    ): Promise<void> => {
      try {
        await axiosInstance.delete(
          `${API_BASE_URL}/reference/api/FavoriteMedicalCard/DeleteFavoriteMedicalCard/${params.id}`
        );
      } catch (error) {
        console.error("Error deleting favorite medical card:", error);
        throw error;
      }
    },
  },

  hospitalBuilding: {
    getAllHospitalBuildings: async (
      params?: GetAllHospitalBuildingsPathParamsDto
    ): Promise<GetAllHospitalBuildingsResponseDto> => {
      try {
        const offId = params?.offId !== undefined ? params.offId : -1;
        const response =
          await axiosInstance.get<GetAllHospitalBuildingsResponseDto>(
            `${API_BASE_URL}/reference/api/HospitalBuilding/GetAllHospitalBuildings/${offId}`
          );
        return response.data;
      } catch (error) {
        console.error("Error fetching all hospital buildings:", error);
        throw error;
      }
    },
  },

  hospitalFloors: {
    getAllHospitalFloorsByHbId: async (
      params: GetAllHospitalFloorsByHbIdPathParamsDto
    ): Promise<HospitalFloorResponseDto[]> => {
      try {
        const { hbId, ...rest } = params;
        const response =
          await axiosInstance.get<HospitalFloorResponseDto[]>(
            `${API_BASE_URL}/reference/api/HospitalFloors/GetAllHospitalFloorsByHbId/${hbId}`,
            { params: rest }
          );
        return response.data;
      } catch (error) {
        console.error(
          "Error fetching all hospital floors:",
          error
        );
        throw error;
      }
    },
  },

  hospitalRoom: {
    getAllHospitalRooms: async (
      params?: GetAllHospitalRoomsPathParamsDto
    ): Promise<GetAllHospitalRoomsResponseDto> => {
      try {
        const hf_Id = params?.hf_Id !== undefined ? params.hf_Id : -1;
        const response =
          await axiosInstance.get<GetAllHospitalRoomsResponseDto>(
            `${API_BASE_URL}/reference/api/HospitalRoom/GetAllHospitalRooms/${hf_Id}`
          );
        return response.data;
      } catch (error) {
        console.error("Error fetching all hospital rooms:", error);
        throw error;
      }
    },
  },

  hrEducation: {
    getAllHrEducationByActive: async (
      params: GetAllHrEducationByActivePathParamsDto
    ): Promise<GetAllHrEducationByActiveResponseDto> => {
      try {
        const response =
          await axiosInstance.get<GetAllHrEducationByActiveResponseDto>(
            `${API_BASE_URL}/reference/api/HrEducation/GetAllHrEducationByActive/${params.isActive}`
          );
        return response.data;
      } catch (error) {
        console.error(
          "Error fetching all HR education by active status:",
          error
        );
        throw error;
      }
    },
  },

  identificationType: {
    getAllIdentificationTypes:
      async (): Promise<GetAllIdentificationTypesResponseDto> => {
        try {
          const response =
            await axiosInstance.get<GetAllIdentificationTypesResponseDto>(
              `${API_BASE_URL}/reference/api/IdentificationType/GetAllIdentificationTypes`
            );
          return response.data;
        } catch (error) {
          console.error("Error fetching all identification types:", error);
          throw error;
        }
      },
  },

  insuredNearness: {
    getAllInsuredNearnesses:
      async (): Promise<GetAllInsuredNearnessesResponseDto> => {
        try {
          const response =
            await axiosInstance.get<GetAllInsuredNearnessesResponseDto>(
              `${API_BASE_URL}/reference/api/InsuredNearness/GetAllInsuredNearnesses`
            );
          return response.data;
        } catch (error) {
          console.error("Error fetching all insured nearnesses:", error);
          throw error;
        }
      },
  },

  insuredType: {
    getAllInsuredTypes: async (): Promise<GetAllInsuredTypesResponseDto> => {
      try {
        const response = await axiosInstance.get<GetAllInsuredTypesResponseDto>(
          `${API_BASE_URL}/reference/api/InsuredType/GetAllInsuredTypes`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching all insured types:", error);
        throw error;
      }
    },
  },

  mcParameter: {
    getAllMcParameters: async (): Promise<GetAllMcParametersResponseDto> => {
      try {
        const response = await axiosInstance.get<GetAllMcParametersResponseDto>(
          `${API_BASE_URL}/reference/api/McParameter/GetAllMcParameters`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching all MC parameters:", error);
        throw error;
      }
    },
  },

  mcParameterDetail: {
    getBulkMcParameterDetails: async (
      data: GetBulkMcParameterDetailsRequestDto
    ): Promise<GetBulkMcParameterDetailsResponseDto> => {
      try {
        // 0 değerlerini filtrele (API 0'dan büyük değerler istiyor)
        const filteredMcpIds = data.mcpIds.filter(id => id > 0);
        
        // Eğer geçerli ID yoksa, boş response dön
        if (filteredMcpIds.length === 0) {
          return {
            data: [],
            issuccess: true,
            statuscode: 200,
            message: '',
          };
        }
        
        const response = await axiosInstance.post<GetBulkMcParameterDetailsResponseDto>(
          `${API_BASE_URL}/reference/api/McParameterDetail/GetMcParameterDetailsbyMc`,
          filteredMcpIds
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching bulk MC parameter details:", error);
        throw error;
      }
    },
  },

  medCard: {
    getAllMedCards: async (): Promise<GetAllMedCardsResponseDto> => {
      try {
        const response = await axiosInstance.get<GetAllMedCardsResponseDto>(
          `${API_BASE_URL}/reference/api/MedCard/GetAllMedCards`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching all med cards:", error);
        throw error;
      }
    },
  },

  mcBradenFile: {
    upload: async (data: UploadBradenFileRequestDto): Promise<McBradenFileResponseDto> => {
      const formData = new FormData();
      if (data.mnypsdId) formData.append('mnypsdId', data.mnypsdId.toString());
      if (data.mpsrbdId) formData.append('mpsrbdId', data.mpsrbdId.toString());
      formData.append('prsdId', data.prsdId.toString());
      formData.append('ptId', data.ptId.toString());
      formData.append('offId', data.offId.toString());
      formData.append('file', data.file);

      try {
        const response = await axiosInstance.post<McBradenFileResponseDto>(
          `${API_BASE_URL}/reference/api/McBradenFile/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error uploading Braden file:", error);
        throw error;
      }
    },
    list: async (id: number): Promise<McBradenFileItemsResponseDto> => {
      try {
        const response = await axiosInstance.get<McBradenFileItemsResponseDto>(
          `${API_BASE_URL}/reference/api/McBradenFile/list/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error listing Braden files:", error);
        throw error;
      }
    },
    get: async (id: number): Promise<McBradenFileResponseDto> => {
      try {
        const response = await axiosInstance.get<McBradenFileResponseDto>(
          `${API_BASE_URL}/reference/api/McBradenFile/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching Braden file:", error);
        throw error;
      }
    },
    update: async (id: number, data: UpdateBradenFileRequestDto): Promise<McBradenFileResponseDto> => {
      const formData = new FormData();
      formData.append('ptId', data.ptId.toString());
      formData.append('offId', data.offId.toString());
      formData.append('file', data.file);

      try {
        const response = await axiosInstance.put<McBradenFileResponseDto>(
          `${API_BASE_URL}/reference/api/McBradenFile/${id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error updating Braden file:", error);
        throw error;
      }
    },
    delete: async (id: number): Promise<McBradenFileResponseDto> => {
      try {
        const response = await axiosInstance.delete<McBradenFileResponseDto>(
          `${API_BASE_URL}/reference/api/McBradenFile/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error deleting Braden file:", error);
        throw error;
      }
    }
  },

  patient: {
    // Hasta isim-soyisim alan endpoint
    getPatientNameSurnames: async (
      data: GetPatientNameSurnamesRequestDto
    ): Promise<GetPatientNameSurnamesResponseDto> => {
      try {
        const response = await axiosInstance.post<GetPatientNameSurnamesResponseDto>(
          `${API_BASE_URL}/reference/api/Patient/GetPatientNameSurnames`,
          data
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient name surnames:", error);
        throw error;
      }
    },

    // Hasta takibi listeleme
    GetPatientMonitoring: async (
      payload: GetPatientMonitoringRequestDto
    ): Promise<GetPatientMonitoringListResponseDto> => {
      try {
        const response = await axiosInstance.get<GetPatientMonitoringListResponseDto>(
          `${API_BASE_URL}/reference/api/Patient/GetPatientMonitoring`,
          { params: payload }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient monitoring:", error);
        throw error;
      }
    },

    // Hasta boy-kilo trace listeleme
    GetPatientHeightWeightTrace: async (
      payload: GetPatientHeightWeightTraceRequestDto
    ): Promise<GetPatientHeightWeightTraceResponseDto> => {
      try {
        const response =
          await axiosInstance.post<GetPatientHeightWeightTraceResponseDto>(
            `${API_BASE_URL}/reference/api/PatientHeightWeightTrace/GetPatientHeightWeightTrace`,
            payload
          );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient height-weight trace:", error);
        throw error;
      }
    },

    // Hasta boy-kilo trace tek kayıt çekme
    GetPatientHeightWeightTraceById: async (
      id: number
    ): Promise<GetPatientHeightWeightTraceByIdResponseDto> => {
      try {
        const response = await axiosInstance.get<GetPatientHeightWeightTraceByIdResponseDto>(
          `${API_BASE_URL}/reference/api/PatientHeightWeightTrace/GetPatientHeightWeightTraceById`,
          { params: { id } }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient height-weight trace by id:", error);
        throw error;
      }
    },

    // Hasta boy-kilo trace ekleme
    AddPatientHeightWeightTrace: async (
      payload: AddPatientHeightWeightTraceRequestDto
    ): Promise<number | any> => {
      try {
        const response = await axiosInstance.post(
          `${API_BASE_URL}/reference/api/PatientHeightWeightTrace/AddPatientHeightWeightTrace`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error adding patient height-weight trace:", error);
        throw error;
      }
    },

    // Hasta boy-kilo trace güncelleme
    UpdatePatientHeightWeightTrace: async (
      id: number,
      payload: UpdatePatientHeightWeightTraceRequestDto
    ): Promise<any> => {
      try {
        const response = await axiosInstance.put(
          `${API_BASE_URL}/reference/api/PatientHeightWeightTrace/UpdatePatientHeightWeightTrace/${id}`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error updating patient height-weight trace:", error);
        throw error;
      }
    },

    // Hasta boy-kilo trace silme
    DeletePatientHeightWeightTrace: async (id: number): Promise<any> => {
      try {
        const response = await axiosInstance.delete(
          `${API_BASE_URL}/reference/api/PatientHeightWeightTrace/DeletePatientHeightWeightTrace/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error deleting patient height-weight trace:", error);
        throw error;
      }
    },

    // Yeni takip ekleme
    AddPatientMonitoring: async (
      payload: CreatePatientMonitoringRequestDto
    ): Promise<number> => {
      try {
        const response = await axiosInstance.post<number>(
          `${API_BASE_URL}/reference/api/Patient/AddPatientMonitoring`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error adding patient monitoring:", error);
        throw error;
      }
    },

    // Takip güncelleme
    UpdatePatientMonitoring: async (
      id: number,
      payload: UpdatePatientMonitoringRequestDto
    ): Promise<number> => {
      try {
        const response = await axiosInstance.put<number>(
          `${API_BASE_URL}/reference/api/Patient/UpdatePatientMonitoring/${id}`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error updating patient monitoring:", error);
        throw error;
      }
    },

    // Takip silme
    DeletePatientMonitoring: async (
      id: number
    ): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await axiosInstance.delete(
          `${API_BASE_URL}/reference/api/Patient/DeletePatientMonitoring/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error deleting patient monitoring:", error);
        throw error;
      }
    },
  },
  patientTypes: {
    getPatientTypes: async (
      params?: GetPatientTypesParamsDto
    ): Promise<GetPatientTypesResponseDto> => {
      try {
        const response =
          await axiosInstance.get<GetPatientTypesResponseDto>(
            `${API_BASE_URL}/reference/api/PatientType/GetPatientTypes`,
            { params }
          );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient types:", error);
        throw error;
      }
    },
  },

  patientDischargeType: {
    getAllPatientDischargeTypes:
      async (): Promise<GetAllPatientDischargeTypeResponseDto> => {
        try {
          const response =
            await axiosInstance.get<GetAllPatientDischargeTypeResponseDto>(
              `${API_BASE_URL}/reference/api/PatientDischargeType/GetAllPatientDischargeTypes`
            );
          return response.data;
        } catch (error) {
          console.error("Error fetching all patient discharge types:", error);
          throw error;
        }
      },
  },

  patientDisease: {
    post: async (
      data: PostPatientDiseaseRequestDto
    ): Promise<PatientDiseaseResponseDto> => {
      try {
        const response = await axiosInstance.post<PatientDiseaseResponseDto>(
          `${API_BASE_URL}/reference/api/PatientDisease`,
          data
        );
        return response.data;
      } catch (error) {
        console.error("Error posting patient disease:", error);
        throw error;
      }
    },
  },

  patientGroup: {
    getPatientGroupByPtype: async (
      params: GetPatientGroupByPtypePathParamsDto
    ): Promise<GetPatientGroupByPtypeResponseDto> => {
      try {
        const response =
          await axiosInstance.get<GetPatientGroupByPtypeResponseDto>(
            `${API_BASE_URL}/reference/api/PatientGroup/GetPatientGroupByPtype/${params.ptype}`
          );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient group by patient type:", error);
        throw error;
      }
    },
  },

  professionList: {
    getAllProfessionList:
      async (): Promise<GetAllProfessionListResponseDto> => {
        try {
          const response =
            await axiosInstance.get<GetAllProfessionListResponseDto>(
              `${API_BASE_URL}/reference/api/ProfessionList/GetAllProfessionList`
            );
          return response.data;
        } catch (error) {
          console.error("Error fetching all profession list:", error);
          throw error;
        }
      },
  },

  reportGenerator: {
    generate: async (orId: number, comId: number): Promise<any> => {
      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/reference/api/ReportGenerator/generate`,
          {
            params: { orId, comId },
            responseType: 'arraybuffer' // Important for handling binary data
          }
        );
        return response;
      } catch (error) {
        console.error("Error generating report:", error);
        throw error;
      }
    }
  },

  referenceList: {
    getAllReferenceList: async (): Promise<GetAllReferenceListResponseDto> => {
      try {
        const response =
          await axiosInstance.get<GetAllReferenceListResponseDto>(
            `${API_BASE_URL}/reference/api/ReferenceList/GetAllReferenceList`
          );
        return response.data;
      } catch (error) {
        console.error("Error fetching all reference list:", error);
        throw error;
      }
    },

    getAuthorizedWarehouses: async (
      offId: number
    ): Promise<GetAuthorizedWarehousesResponseDto> => {
      try {
        const response = await axiosInstance.get<AuthorizedWarehouseDto[]>(
          `${API_BASE_URL}/reference/api/ReferenceList/GetAuthorizedWarehouses`,
          { params: { OffId: offId } }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching authorized warehouses:", error);
        throw error;
      }
    },
  },

  speecialCode: {
    getSpecialCodeListByPartNumber: async (
      params: GetSpecialCodeListByPartNumberPathParamsDto
    ): Promise<GetSpecialCodeListByPartNumberResponseDto> => {
      try {
        const response =
          await axiosInstance.get<GetSpecialCodeListByPartNumberResponseDto>(
            `${API_BASE_URL}/reference/api/SpeecialCode/GetSpecialCodeListByPartNumber/${params.partNumber}`
          );
        return response.data;
      } catch (error) {
        console.error(
          "Error fetching special code list by part number:",
          error
        );
        throw error;
      }
    },
  },

  user: {
    getAllUsers: async (): Promise<GetAllUsersResponseDto> => {
      try {
        const response = await axiosInstance.get<GetAllUsersResponseDto>(
          `${API_BASE_URL}/reference/api/User/GetAllUsers`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching all users:", error);
        throw error;
      }
    },
  },

  processStatus: {
    GetProcessStatusByType: async (typeId: number) => {
      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/reference/api/ProcessStatus/GetProcessStatusByType/${typeId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching process status by type:", error);
        throw error;
      }
    },
  },
  patientVitalRecord: {
    // 🔍 Bir hastanın yaşam bulgularını PtId veya UPN ile listeleme
    GetPatientVitalRecord: async (
      payload: GetPatientVitalRecordRequestDto
    ): Promise<GetPatientVitalRecordResponseDto> => {
      try {
        const response = await axiosInstance.get<GetPatientVitalRecordResponseDto>(
          `${API_BASE_URL}/reference/api/Patient/GetPatientVitalRecord`,
          { params: payload }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient vital records:", error);
        throw error;
      }
    },

    // 🔍 ID'ye göre yaşam bulgusu tek kayıt getirme
    GetPatientVitalRecordById: async (
      id: number
    ): Promise<GetPatientVitalRecordByIdResponseDto> => {
      try {
        const response = await axiosInstance.get<GetPatientVitalRecordByIdResponseDto>(
          `${API_BASE_URL}/reference/api/Patient/GetPatientVitalRecordById/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient vital record by id:", error);
        throw error;
      }
    },

    // ➕ Yeni yaşam bulgusu ekleme
    AddPatientVitalRecord: async (
      payload: CreatePatientVitalRecordRequestDto
    ): Promise<number> => {
      try {
        const response = await axiosInstance.post<number>(
          `${API_BASE_URL}/reference/api/Patient/AddPatientVitalRecord`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error adding patient vital record:", error);
        throw error;
      }
    },

    // ✏️ Yaşam bulgusu güncelleme
    UpdatePatientVitalRecord: async (
      id: number,
      payload: UpdatePatientVitalRecordRequestDto
    ): Promise<number> => {
      try {
        const response = await axiosInstance.put<number>(
          `${API_BASE_URL}/reference/api/Patient/UpdatePatientVitalRecord/${id}`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error updating patient vital record:", error);
        throw error;
      }
    },

    // 🗑️ Yaşam bulgusu silme
    DeletePatientVitalRecord: async (
      id: number
    ): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await axiosInstance.delete(
          `${API_BASE_URL}/reference/api/Patient/DeletePatientVitalRecord/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error deleting patient vital record:", error);
        throw error;
      }
    },
  },


  newbornTrace: {
    get: async (
      params: GetNewbornTraceRequestDto
    ): Promise<GetNewbornTraceResponseDto> => {
      try {
        const response = await axiosInstance.get<GetNewbornTraceResponseDto>(
          `${API_BASE_URL}/reference/api/NewbornTrace/GetNewbornTrace`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching newborn trace:", error);
        throw error;
      }
    },

    getById: async (
      id: number
    ): Promise<NewbornTraceItemDto> => {
      try {
        const response = await axiosInstance.get<NewbornTraceItemDto>(
          `${API_BASE_URL}/reference/api/NewbornTrace/GetNewbornTraceById/${id}`
        );
        const raw = response.data as any;
        // API bazen string dönebiliyor
        const parsed =
          typeof raw === "string"
            ? JSON.parse(raw)
            : (raw as any)?.data ?? raw;
        return parsed as NewbornTraceItemDto;
      } catch (error) {
        console.error("Error fetching newborn trace by id:", error);
        throw error;
      }
    },

    add: async (
      payload: AddNewbornTraceRequestDto
    ): Promise<any> => {
      try {
        const response = await axiosInstance.post(
          `${API_BASE_URL}/reference/api/NewbornTrace/AddNewbornTrace`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error adding newborn trace:", error);
        throw error;
      }
    },

    update: async (
      id: number,
      payload: UpdateNewbornTraceRequestDto
    ): Promise<any> => {
      try {
        const response = await axiosInstance.put(
          `${API_BASE_URL}/reference/api/NewbornTrace/UpdateNewbornTrace/${id}`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error updating newborn trace:", error);
        throw error;
      }
    },

    delete: async (
      id: number
    ): Promise<any> => {
      try {
        const response = await axiosInstance.delete(
          `${API_BASE_URL}/reference/api/NewbornTrace/DeleteNewbornTrace/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error deleting newborn trace:", error);
        throw error;
      }
    },
  },

  additionalInfo: {
    // /reference/api/AdditionalInfo/fields
    getFields: async (
      payload: GetAdditionalInfoFieldsRequestDto
    ): Promise<GetAdditionalInfoFieldsResponseDto> => {
      try {
        const response = await axiosInstance.post<GetAdditionalInfoFieldsResponseDto>(
          `${API_BASE_URL}/reference/api/AdditionalInfo/fields`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching additional info fields:", error);
        throw error;
      }
    },
  },
  patientProcess: {
    getPatientProcessList: async (
      params: GetPatientProcessRequestDto
    ): Promise<GetPatientProcessListResponseDto> => {
      try {
        const response = await axiosInstance.get<GetPatientProcessListResponseDto>(
          `${API_BASE_URL}/reference/api/Patient/GetPatientProcessList`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient process list:", error);
        throw error;
      }
    },

    deletePatientProcess: async (id: number): Promise<void> => {
      try {
        await axiosInstance.delete(`${API_BASE_URL}/reference/api/Patient/DeletePatientProcess/${id}`);
      } catch (error) {
        console.error("Error deleting patient process:", error);
        throw error;
      }
    },
  },

  pediatricAssessment: {
    // GET /api/PediatricAssessment/GetPediatricAssessment
    GetPediatricAssessment: async (
      params: GetPediatricAssessmentRequestDto
    ): Promise<{ data: GetPediatricAssessmentResponseDto[] }> => {
      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/reference/api/PediatricAssessment/GetPediatricAssessment`,
          { params }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching pediatric assessments:", error);
        throw error;
      }
    },

    // GET /api/PediatricAssessment/GetPediatricAssessmentById/{id}
    GetPediatricAssessmentById: async (
      id: number
    ): Promise<{ data: GetPediatricAssessmentResponseDto }> => {
      try {
        const response = await axiosInstance.get(
          `${API_BASE_URL}/reference/api/PediatricAssessment/GetPediatricAssessmentById/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching pediatric assessment by ID:", error);
        throw error;
      }
    },

    // POST /api/PediatricAssessment/AddPediatricAssessment
    AddPediatricAssessment: async (
      payload: CreatePediatricAssessmentRequestDto
    ): Promise<{ data: number }> => {
      try {
        const response = await axiosInstance.post(
          `${API_BASE_URL}/reference/api/PediatricAssessment/AddPediatricAssessment`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error adding pediatric assessment:", error);
        throw error;
      }
    },

    // PUT /api/PediatricAssessment/UpdatePediatricAssessment/{id}
    UpdatePediatricAssessment: async (
      id: number,
      payload: UpdatePediatricAssessmentRequestDto
    ): Promise<{ data: number }> => {
      try {
        const response = await axiosInstance.put(
          `${API_BASE_URL}/reference/api/PediatricAssessment/UpdatePediatricAssessment/${id}`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error("Error updating pediatric assessment:", error);
        throw error;
      }
    },

    // DELETE /api/PediatricAssessment/DeletePediatricAssessment/{id}
    DeletePediatricAssessment: async (
      id: number
    ): Promise<{ data: number }> => {
      try {
        const response = await axiosInstance.delete(
          `${API_BASE_URL}/reference/api/PediatricAssessment/DeletePediatricAssessment/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error deleting pediatric assessment:", error);
        throw error;
      }
    },
  },
};
